/**
 * One cell of the Property, Capital, Owners or Loans sheet: what it is and
 * the workbook cell it was read from, in the layout of the figure panel.
 */

import { ExternalLink, X } from "lucide-react";
import React from "react";
import type { Portfolio } from "../../services/portfolioDataService";
import { workbookCellHref } from "./portfolioModel";
import type { RecordSelection } from "./sheets";

export interface RecordPanelProps {
	portfolio: Portfolio;
	record: RecordSelection;
	onClose: () => void;
	onAsk: () => void;
}

const SECTION_HEADING = "text-xs font-semibold uppercase tracking-[0.06em] text-[#6B7280]";

const RecordPanel: React.FC<RecordPanelProps> = ({ portfolio, record, onClose, onAsk }) => {
	const house = record.houseId
		? portfolio.tables.house.find((h) => h.house_id === record.houseId)
		: null;
	const href = workbookCellHref(portfolio, {
		source_file: record.sourceFile,
		sheet: record.sheet,
		cell: record.cell,
	});
	return (
		<aside
			aria-label="About this cell"
			className="flex w-[420px] flex-shrink-0 flex-col border-l border-gray-200 bg-white shadow-[-12px_0_32px_rgba(17,24,39,0.06)]"
		>
			<div className="flex items-start gap-3 border-b border-[#f1f2f4] px-[22px] pb-4 pt-5">
				<div className="min-w-0 flex-1">
					{record.houseId && (
						<p className="text-xs text-[#6B7280]">
							{record.houseId}
							{house?.address ? ` · ${String(house.address)}` : ""}
						</p>
					)}
					<h2 className="mt-1 break-words text-xl font-semibold text-[#111111]">{record.title}</h2>
				</div>
				<button
					type="button"
					onClick={onClose}
					aria-label="Close cell"
					className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-[#5B6472] hover:bg-gray-100"
				>
					<X className="h-[18px] w-[18px]" aria-hidden />
				</button>
			</div>
			<div className="flex min-h-0 flex-1 flex-col gap-[18px] overflow-y-auto px-[22px] py-[18px]">
				<div className="flex flex-wrap items-baseline gap-3">
					<span className="break-words text-[24px] font-semibold tabular-nums text-[#111111]">
						{record.value || "—"}
					</span>
					<span className="inline-flex h-[22px] items-center rounded-full bg-[#E6F3F3] px-2 text-[11.5px] font-semibold text-[#004C4B]">
						From your workbook
					</span>
				</div>

				{record.rows && record.rows.length > 0 && (
					<dl className="grid grid-cols-[minmax(0,1fr)_auto] gap-x-3 gap-y-1.5 text-[13px]">
						{record.rows.map((row) => (
							<React.Fragment key={row.label}>
								<dt className="text-[#5B6472]">{row.label}</dt>
								<dd
									className={`text-right tabular-nums ${row.tone === "attention" ? "font-semibold text-[#b54708]" : "text-[#1F2937]"}`}
								>
									{row.value}
								</dd>
							</React.Fragment>
						))}
					</dl>
				)}
				{record.note && (
					<p className="rounded-lg border border-[#fedf89] bg-[#fffaeb] px-3 py-2 text-[12.5px] leading-relaxed text-[#7a2e0e]">
						{record.note}
					</p>
				)}

				<section className="flex flex-col gap-2">
					<h3 className={SECTION_HEADING}>Where it's from in your workbook</h3>
					<div className="flex flex-col gap-1.5 rounded-[10px] border border-[#eceae6] bg-[#f7f7f6] px-3 py-2.5">
						<span className="font-mono text-[12.5px] text-[#1F2937]">
							{record.sheet}!{record.cell}
						</span>
						{record.header && <span className="text-[12.5px] text-[#5B6472]">Column “{record.header}”</span>}
						<span className="text-xs text-[#6B7280]">{record.sourceFile}</span>
					</div>
					{href ? (
						<a
							href={href}
							target="_blank"
							rel="noreferrer"
							className="inline-flex items-center gap-1.5 self-start py-1 text-[13px] font-medium text-[#004C4B] underline underline-offset-[3px]"
						>
							Open in workbook
							<ExternalLink className="h-3.5 w-3.5" aria-hidden />
						</a>
					) : (
						<span className="text-xs text-[#6B7280]">
							This workbook isn't in the Deal Room, so it can't be opened here.
						</span>
					)}
				</section>

				<button
					type="button"
					onClick={onAsk}
					className="mt-auto inline-flex h-9 items-center self-start rounded-lg border border-[#99CFCD] bg-white px-3.5 text-sm font-medium text-[#004C4B] hover:bg-[#f6fbfb]"
				>
					Ask about this
				</button>
			</div>
		</aside>
	);
};

export default RecordPanel;
